/**
 * Place filters sales can trust. Each chip is one Mapbox poi-label icon.
 * Food retail covers supermarkets and hypermarkets, a highway stop covers rest
 * areas and service areas, and shops cover malls and other retail. Furniture
 * and DIY is the big store a retail park is built around. Other is every
 * remaining icon; its marker is drawn on the chip only.
 */
export const PLACE_SITE_TYPES = [
  { id: 'airport', label: 'Airport', placeClass: '', makis: ['airport'] },
  { id: 'gas-station', label: 'Gas station', placeClass: 'motorist', makis: ['fuel'] },
  { id: 'hotel', label: 'Hotel', placeClass: 'lodging', makis: ['lodging'] },
  { id: 'fast-food', label: 'Fast food', placeClass: 'food_and_drink', makis: ['fast-food'] },
  {
    id: 'parking-paid',
    label: 'Paid parking',
    placeClass: 'motorist',
    makis: ['parking-paid'],
  },
  {
    id: 'food-retail',
    label: 'Food retail',
    placeClass: 'food_and_drink_stores',
    makis: ['grocery'],
  },
  { id: 'highway-stop', label: 'Highway stop', placeClass: '', makis: ['highway-rest-area'] },
  { id: 'shops', label: 'Shops', placeClass: 'store_like', makis: ['shop'] },
  {
    id: 'furniture-diy',
    label: 'Furniture and DIY',
    placeClass: 'store_like',
    makis: ['furniture', 'hardware'],
  },
] as const;

/** Categories that are not one of the site types above. `icon` is the chip, not a filter. */
export const OTHER_PLACE_FILTER = {
  id: 'other',
  label: 'Other',
  placeClass: '',
  makis: [] as readonly string[],
  icon: 'marker',
} as const;

/** One chip per site type, then Other. The first maki is the chip's icon. */
export const PLACE_FILTER_CHIPS = [...PLACE_SITE_TYPES, OTHER_PLACE_FILTER] as const;

export type PlaceSiteTypeId = (typeof PLACE_FILTER_CHIPS)[number]['id'];

export type PlaceVisibility = Record<PlaceSiteTypeId, boolean>;

export const PLACE_SITE_TYPE_IDS: readonly PlaceSiteTypeId[] = PLACE_FILTER_CHIPS.map(
  (chip) => chip.id,
);

export const ALL_PLACES_VISIBLE: PlaceVisibility = Object.fromEntries(
  PLACE_SITE_TYPE_IDS.map((id) => [id, true]),
) as PlaceVisibility;

/** Glyph on the chip. Other's marker is not one of the icons the map filters. */
export function placeChipMaki(chip: { makis: readonly string[]; icon?: string }): string {
  return chip.makis[0] ?? chip.icon ?? '';
}

/** The chip a basemap icon belongs to. Anything outside the site types is Other. */
export function placeSiteTypeId(maki: string): PlaceSiteTypeId {
  const match = PLACE_SITE_TYPES.find((siteType) =>
    (siteType.makis as readonly string[]).includes(maki),
  );
  return match?.id ?? OTHER_PLACE_FILTER.id;
}
